import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, MessageSquare } from "lucide-react";

const CATEGORIES = ["Maintenance", "Food", "Wi-Fi", "Cleanliness", "Other"];

export default function ComplaintFormDialog({ open, onOpenChange, onSaved }) {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [category, setCategory] = useState("Maintenance");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStudentId("");
    setCategory("Maintenance");
    setDescription("");
    api
      .get("/students")
      .then((r) => setStudents(r.data || []))
      .catch(() => setStudents([]));
  }, [open]);

  const save = async () => {
    if (!studentId) {
      toast.error("Please pick a student");
      return;
    }
    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/complaints", {
        student_id: studentId,
        category,
        description,
      });
      toast.success("Complaint added");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add complaint");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg border-2 border-[#0A0A0A] shadow-brutal rounded-xl bg-white"
        data-testid="complaint-form-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 border-2 border-[#0A0A0A] rounded-xl bg-[#00E5FF]">
              <MessageSquare size={22} strokeWidth={2.5} />
            </div>
            <div>
              <DialogTitle className="font-heading font-black text-2xl tracking-tighter">
                Add Complaint
              </DialogTitle>
              <DialogDescription className="text-sm font-medium text-[#4A4A4A]">
                Log a complaint on behalf of a resident.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
              Student <span className="text-[#FF4D4D]">*</span>
            </label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger
                data-testid="complaint-form-student"
                className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-bold"
              >
                <SelectValue placeholder="Pick a student" />
              </SelectTrigger>
              <SelectContent className="border-2 border-[#0A0A0A] max-h-72">
                {students.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.full_name}
                    {s.flat_number ? ` · ${s.flat_number}` : ""}
                    {s.student_id_number ? ` · ${s.student_id_number}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
              Category
            </label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                data-testid="complaint-form-category"
                className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-bold"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-2 border-[#0A0A0A]">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
              Description <span className="text-[#FF4D4D]">*</span>
            </label>
            <textarea
              data-testid="complaint-form-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
              placeholder="What happened?"
            />
          </div>
        </div>

        <DialogFooter className="mt-4 gap-3">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="complaint-form-cancel"
            className="px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-white"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="complaint-form-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
          >
            <Plus size={16} strokeWidth={2.5} />
            {saving ? "Adding..." : "Add complaint"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
